/**
 * The browser half of the live feed.
 *
 * Uses the platform's own `EventSource` rather than a hand-rolled fetch loop, and that is the
 * decision the whole realtime design rests on: EventSource reconnects by itself, and when it does it
 * automatically sends back the `id:` of the last event it saw as a `Last-Event-ID` header. The
 * backend replays from there. Nothing in this file implements reconnection, sequence tracking or
 * gap detection — the platform does it, correctly, for free. Writing it by hand over WebSockets
 * would be reimplementing all three.
 *
 * Callbacks are held in refs so a parent re-render does not tear the connection down and rebuild it.
 * Only the URL — that is, the filters — is allowed to do that.
 */

import { useEffect, useRef, useState } from "react";
import type { ConnectionState, LogRow, PunishmentEvent, VoiceEvent } from "../types";

interface Options {
  /** Query string for the stream, already filtered. A change here reconnects. */
  query: string;
  /** Live tail toggle. False closes the connection entirely rather than just ignoring events —
   *  an idle stream still costs the server a connection against its per-user cap. */
  enabled: boolean;
  onLog?: (row: LogRow) => void;
  onVoice?: (event: VoiceEvent) => void;
  onPunishment?: (event: PunishmentEvent) => void;
  /** The server could not guarantee continuity. The correct response is a clean refetch, not an
   *  attempt to patch the buffer. */
  onGap?: (reason: string) => void;
  /** Somebody signed in to this account from a new device. */
  onLoginAlert?: (alert: { id: number; device: string; location: string; time: number }) => void;
}

export function useLiveStream(options: Options): { state: ConnectionState } {
  const [state, setState] = useState<ConnectionState>("offline");

  const handlers = useRef(options);
  handlers.current = options;

  const { query, enabled } = options;

  useEffect(() => {
    if (!enabled) {
      setState("offline");
      return;
    }

    const source = new EventSource(`/api/logs/stream?${query}`, { withCredentials: true });

    source.addEventListener("open", () => setState("live"));

    source.addEventListener("log", (event) => {
      handlers.current.onLog?.(JSON.parse((event as MessageEvent<string>).data) as LogRow);
    });

    source.addEventListener("voice", (event) => {
      handlers.current.onVoice?.(JSON.parse((event as MessageEvent<string>).data) as VoiceEvent);
    });

    source.addEventListener("punishment", (event) => {
      handlers.current.onPunishment?.(
        JSON.parse((event as MessageEvent<string>).data) as PunishmentEvent,
      );
    });

    source.addEventListener("login-alert", (event) => {
      handlers.current.onLoginAlert?.(JSON.parse((event as MessageEvent<string>).data));
    });

    // The server's own view of the pipeline: whether it is reading Redis or has fallen back to
    // polling MySQL. Surfaced verbatim, because "degraded" is something staff should be able to see
    // rather than something to hide behind a green dot.
    source.addEventListener("state", (event) => {
      const payload = JSON.parse((event as MessageEvent<string>).data) as { state: ConnectionState };
      setState(payload.state);
    });

    source.addEventListener("gap", (event) => {
      const payload = JSON.parse((event as MessageEvent<string>).data) as { reason?: string };
      handlers.current.onGap?.(payload.reason ?? "unknown");
    });

    source.onerror = () => {
      // EventSource retries on its own. CLOSED means it has given up — which for us means the
      // session was revoked or the server rejected the connection, and only a reload will help.
      setState(source.readyState === EventSource.CLOSED ? "offline" : "reconnecting");
    };

    return () => source.close();
  }, [query, enabled]);

  return { state };
}
